import React, { useCallback } from 'react';

import { CustomGridColumns } from '~/components/CustomDataGrid/protocols';
import { tableItensColumns } from './TableItens';
import {
  TableContainer,
  Container,
  ButtonRow,
  ButtonRowContainer,
} from './styles';
import { ScrollButtons } from './ScrollButtons';
import { useEntradaNFE } from '~/pages/EntradaNFE/EntradaNFEContext';
import { GoPencil } from 'react-icons/go';
import { FaTrash } from 'react-icons/fa';
import { Util } from '~/pages/EntradaNFE/functions/Util';
import { Produto } from '~/pages/EntradaNFE/protocols';
import { formatCurrencyAsText, moneyFormat } from '~/utils/functions';
import Swal from 'sweetalert2';
import { deleteItens } from '~/utils/masterDetail';
import CustomDataGrid from '../CustomDataGrid';

export const TableItens: React.FC = () => {
  const {
    produtos,
    setProdutos,
    setProduto,
    masterDetail,
    setMasterDetail,
    loadingTable,
    produto,
    setProdutoModalNaoCalcBc,
    setShowModalNaoCalcBc,
    canUpdate,
    setUuidItens,
    formItem,
    codXml,
    setCodIbsCbsRetriever,
    formNfe,
  } = useEntradaNFE();

  const resolveIbsCbsCode = (raw: unknown): number | null => {
    if (raw === null || raw === undefined) return null;

    if (typeof raw === 'number') {
      if (!Number.isFinite(raw) || raw <= 0) return null;
      return raw;
    }

    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (trimmed === '') return null;
      const num = Number(trimmed);
      if (!Number.isFinite(num) || num <= 0) return null;
      return num;
    }

    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return resolveIbsCbsCode((raw as any)?.value);
    }

    return null;
  };

  const updateProdutos = useCallback(() => {
    const updatedProdutos = produtos.map((p) => {
      const cfopSegment = p.cfop.slice(1, 4);

      const produtoFormatado = {
        ...p,
        val_bc_ibs_cbs: moneyFormat(String(p.val_bc_ibs_cbs)),
        val_cbs: moneyFormat(String(p.val_cbs)),
        val_ibs_mun: moneyFormat(String(p.val_ibs_mun)),
        val_ibs_est: moneyFormat(String(p.val_ibs_est)),
        per_aliq_cbs: moneyFormat(String(p.per_aliq_cbs)),
        per_aliq_ibs_est: moneyFormat(String(p.per_aliq_ibs_est)),
        per_aliq_ibs_mun: moneyFormat(String(p.per_aliq_ibs_mun)),
        per_red_cbs: moneyFormat(String(p.per_red_cbs)),
        per_red_ibs: moneyFormat(String(p.per_red_ibs)),
        val_red_social: moneyFormat(String(p.val_red_social)),
        val_imp_ibs_cbs: moneyFormat(String(p.val_imp_ibs_cbs)),
        val_custo_sibscbs: moneyFormat(String(p.val_custo_sibscbs)),
        val_custo_sibscbs_fisc: moneyFormat(String(p.val_custo_sibscbs_fisc)),
      };

      if (cfopSegment === '910' || cfopSegment === '920') {
        return { ...produtoFormatado, flg_bonificado: true };
      }

      return { ...produtoFormatado, flg_bonificado: false };
    });

    return updatedProdutos;
  }, [produtos]);

  const onFlgNaoCalcBcClick = (params: Produto) => {
    const findProduto = updateProdutos().find((reg) => {
      return reg.num_item === params.num_item;
    });

    if (findProduto) {
      const demaisProdutos = updateProdutos().filter((reg) => {
        return reg.num_item !== params.num_item;
      });
      findProduto.flg_nao_calc_bc = !findProduto.flg_nao_calc_bc;
      const updatedProducts = [...demaisProdutos, findProduto].sort(
        (a, b) => a.num_item - b.num_item,
      );
      setProdutos(updatedProducts);
      setProdutoModalNaoCalcBc(findProduto);
      setShowModalNaoCalcBc(true);
    }
  };

  const onEditItem = (params: any) => {
    setUuidItens({
      uuid: params.uuid,
      cod_seq_nf_item: params.cod_seq_nf_item,
    });
    formItem.setValue('des_referencia', params.des_referencia);

    formItem.setValue('des_unidade', {
      value: params.des_unidade.trim(),
      label: params.des_unidade.trim(),
    });
    formItem.setValue('cod_tributacao', {
      label: '',
      value: params.cod_tributacao,
    });

    formItem.setValue('cfop', {
      label: params.des_cfop,
      value: params.cfop.trim(),
    });

    if (params?.des_unidade.trim() === params?.des_unidade_compra.trim()) {
      formItem.setValue('tipo_embalagem', { label: 'Compra', value: 0 });
    } else {
      formItem.setValue('tipo_embalagem', { label: 'Venda', value: 1 });
    }

    formItem.setValue('qtd_embalagem', params.qtd_embalagem);
    formItem.setValue('qtd_entrada', params.qtd_entrada);
    formItem.setValue('flg_bonificado', params.flg_bonificado);
    formItem.setValue('val_tabela', moneyFormat(params.val_tabela, 4, 4));
    formItem.setValue(
      'val_tot_venda_varejo',
      formatCurrencyAsText(params.val_tot_venda_varejo),
    );
    formItem.setValue(
      'val_tot_desp_acess',
      formatCurrencyAsText(params.val_tot_desp_acess),
    );
    formItem.setValue(
      'val_tot_desconto',
      formatCurrencyAsText(params.val_tot_desconto),
    );
    formItem.setValue(
      'val_tot_acrescimo',
      formatCurrencyAsText(params.val_tot_acrescimo),
    );
    formItem.setValue(
      'val_tot_frete',
      formatCurrencyAsText(params.val_tot_frete),
    );
    formItem.setValue('val_tot_ipi', formatCurrencyAsText(params.val_tot_ipi));
    formItem.setValue('val_tot_st', formatCurrencyAsText(params.val_tot_st));
    const codIbsCbsResolved = resolveIbsCbsCode(params.cod_ibs_cbs);
    formItem.setValue('cod_ibs_cbs', codIbsCbsResolved);
    setCodIbsCbsRetriever(codIbsCbsResolved);

    const {
      cod_gtin_principal,
      cod_id_ctb,
      cod_ncm,
      cod_produto,
      cod_tipo_item,
      cod_trib_entrada,
      cod_tributacao,
      des_produto,
      des_unidade_compra,
      des_unidade_venda,
      flg_ipv,
      flg_nao_pis_cofins,
      num_excecao_tipi,
      num_item,
      num_ncm,
      per_aliq_cofins,
      per_aliq_pis,
      per_desp_op,
      per_fcp_icms,
      per_fcp_st,
      per_icms_saida,
      per_pauta_iva,
      qtd_embalagem_compra,
      qtd_embalagem_venda,
      qtd_est_atual,
      tipo_nao_pis_cofins,
      tipo_trib_entrada,
      val_custo_medio_ant,
      val_custo_rep,
      val_custo_tabela,
      val_pauta_ipv,
      val_pauta_iva,
      val_peso,
      // IBS/CBS
      cod_ibs_cbs,
      val_bc_ibs_cbs,
      val_cbs,
      val_ibs_mun,
      val_ibs_est,
      per_aliq_cbs,
      per_aliq_ibs_est,
      per_aliq_ibs_mun,
      per_red_cbs,
      per_red_ibs,
      val_red_social,
      val_imp_ibs_cbs,
      val_custo_sibscbs,
      val_custo_sibscbs_fisc,
    } = params;

    setProduto({
      ...produto,
      cod_gtin_principal,
      cod_id_ctb,
      cod_ncm,
      cod_produto,
      cod_tipo_item,
      cod_trib_entrada,
      cod_tributacao,
      des_produto,
      des_unidade_compra,
      des_unidade_venda,
      flg_ipv,
      flg_nao_pis_cofins,
      num_excecao_tipi,
      num_item,
      num_ncm,
      per_aliq_cofins,
      per_aliq_pis,
      per_desp_op,
      per_fcp_icms,
      per_fcp_st,
      per_icms_saida,
      per_pauta_iva,
      qtd_embalagem_compra,
      qtd_embalagem_venda,
      qtd_est_atual,
      tipo_nao_pis_cofins,
      tipo_trib_entrada,
      val_custo_medio_ant,
      val_custo_rep,
      val_custo_tabela,
      val_pauta_ipv,
      val_pauta_iva,
      val_peso,
      // IBS/CBS
      cod_ibs_cbs,
      val_bc_ibs_cbs,
      val_cbs,
      val_ibs_mun,
      val_ibs_est,
      per_aliq_cbs,
      per_aliq_ibs_est,
      per_aliq_ibs_mun,
      per_red_cbs,
      per_red_ibs,
      val_red_social,
      val_imp_ibs_cbs,
      val_custo_sibscbs,
      val_custo_sibscbs_fisc,
    });
    formItem.setValue('cod_produto', {
      label: `${cod_produto} - ${des_produto}`,
      value: cod_produto,
    });
  };

  const actions: CustomGridColumns[] = [
    {
      field: 'acoes',
      headerName: 'Ações',
      fixed: true,
      fixedDirection: 'right',
      width: 250,
      renderCell: (params: any) => {
        return (
          <ButtonRowContainer>
            <ButtonRow type="button" title="Ignora Base de Cálculo?">
              <div className="igBc" style={{ cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  disabled={!canUpdate}
                  checked={params.flg_nao_calc_bc}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canUpdate) {
                      onFlgNaoCalcBcClick(params);
                    }
                  }}
                />{' '}
                Ig. Bc?
              </div>
            </ButtonRow>
            <ButtonRow
              type="button"
              onClick={() => onEditItem(params)}
              disabled={!canUpdate || codXml !== undefined}
              title="Editar"
            >
              <GoPencil size={20} style={{ color: '#72ab90' }} />
            </ButtonRow>
            <ButtonRow
              type="button"
              onClick={() => onRemoveItem(params)}
              disabled={!canUpdate || codXml !== undefined}
              title="Remover"
            >
              <FaTrash size={20} style={{ color: '#e63c3c' }} />
            </ButtonRow>
          </ButtonRowContainer>
        );
      },
    },
  ];

  const columns: CustomGridColumns[] = [...tableItensColumns, ...actions];

  const onRemoveItem = (params: any) => {
    Swal.fire({
      title: ``,
      text: 'Deseja realmente excluir esse item?',

      showCancelButton: true,
      confirmButtonColor: '#07289e',
      cancelButtonColor: '#ff7b7b',
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
    }).then(async (result) => {
      if (result.isConfirmed) {
        const { uuid } = params;

        const produtosData: any[] = await deleteItens(
          'nf_itens',
          uuid,
          masterDetail,
          setMasterDetail,
        );
        const produtosReordenados = Util.reordenarProdutos(produtosData);
        setProdutos(produtosReordenados);
        setUuidItens(undefined);
        formNfe.clearErrors('cod_serie');
      }
    });
  };

  return (
    <Container>
      <ScrollButtons />
      <TableContainer>
        <CustomDataGrid
          loading={loadingTable}
          rows={updateProdutos()}
          columns={columns}
        />
      </TableContainer>
    </Container>
  );
};
